import { useEffect, useRef, useState } from "react";
import { withTranslation, TFunction } from "react-i18next";
import Container from "../../common/Container";
import { SvgIcon } from "../../common/SvgIcon";
import { useHistory } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import {
  HeaderSection,
  HeaderRow,
  LogoContainer,
  LogoWrapper,
  WordmarkText,
  Burger,
  NotHidden,
  Menu,
  NavItem,
  NavCta,
  Label,
  DrawerOverlay,
  DrawerPanel,
  DrawerClose,
  AccountWrap,
  AvatarButton,
  AccountMenu,
  AccountEmail,
  AccountMenuItem,
  DrawerDivider,
  DrawerUserEmail,
} from "./styles";

const Header = ({ t }: { t: TFunction }) => {
  const [visible, setVisibility] = useState(false);
  const [accountOpen, setAccountOpen] = useState(false);
  const accountRef = useRef<HTMLDivElement>(null);
  const history = useHistory();
  const { user, logout } = useAuth();

  const toggleButton = () => setVisibility((v) => !v);

  const handleLogoClick = () => history.push("/");

  // Close the account dropdown when clicking anywhere else.
  useEffect(() => {
    if (!accountOpen) return;
    const handleClick = (e: MouseEvent) => {
      if (accountRef.current && !accountRef.current.contains(e.target as Node)) {
        setAccountOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, [accountOpen]);

  const goTo = (path: string) => {
    history.push(path);
    setVisibility(false);
    setAccountOpen(false);
  };

  const handleLogout = () => {
    logout();
    setAccountOpen(false);
    setVisibility(false);
    history.push("/");
  };

  const avatarInitial = (user?.email || user?.username || "?").charAt(0);

  const MenuItem = () => (
    <>
      <NavItem onClick={() => goTo("/quizzes")}>Modules</NavItem>
      {/* Free-for-now: pricing hidden. Restore when payments launch.
      <NavItem onClick={() => goTo("/pricing")}>Pricing</NavItem> */}
      <NavCta onClick={() => goTo("/quizzes")}>Start Practising →</NavCta>
    </>
  );

  return (
    <>
      <HeaderSection>
        {/* clip={false}: the account dropdown is absolutely positioned and
            extends below the header — Container's default overflow:hidden
            silently clipped it even though it was toggling open correctly. */}
        <Container clip={false}>
          <HeaderRow>
            <LogoContainer onClick={handleLogoClick}>
              <LogoWrapper>
                <SvgIcon src="logo.svg" width="34px" height="34px" />
                <WordmarkText>
                  <span className="wm-my">My</span>
                  <span className="wm-exam">Exam</span>
                  <span className="wm-nation">Nation</span>
                </WordmarkText>
              </LogoWrapper>
            </LogoContainer>
            <NotHidden>
              <Menu>
                <NavItem onClick={() => goTo("/quizzes")}>Modules</NavItem>
                {/* Free-for-now: pricing hidden. Restore when payments launch.
                <NavItem onClick={() => goTo("/pricing")}>Pricing</NavItem> */}
                {!user && (
                  <NavItem onClick={() => goTo("/signin")}>Sign in</NavItem>
                )}
                <NavCta onClick={() => goTo("/quizzes")}>
                  Start Practising →
                </NavCta>
                {user && (
                  <AccountWrap ref={accountRef}>
                    <AvatarButton
                      onClick={() => setAccountOpen((o) => !o)}
                      aria-label="Account menu"
                      aria-expanded={accountOpen}
                    >
                      {avatarInitial}
                    </AvatarButton>
                    <AccountMenu $open={accountOpen}>
                      <AccountEmail>{user.email}</AccountEmail>
                      <AccountMenuItem onClick={() => goTo("/progress")}>
                        My Progress
                      </AccountMenuItem>
                      <AccountMenuItem onClick={() => goTo("/account")}>
                        My Account
                      </AccountMenuItem>
                      <AccountMenuItem onClick={handleLogout}>
                        Sign out
                      </AccountMenuItem>
                    </AccountMenu>
                  </AccountWrap>
                )}
              </Menu>
            </NotHidden>
            <Burger onClick={toggleButton} aria-label="Open menu">
              <svg width="22" height="16" viewBox="0 0 22 16" fill="none" aria-hidden="true">
                <rect width="22" height="2" rx="1" fill="#6E89AA" />
                <rect y="7" width="22" height="2" rx="1" fill="#6E89AA" />
                <rect y="14" width="22" height="2" rx="1" fill="#6E89AA" />
              </svg>
            </Burger>
          </HeaderRow>
        </Container>
      </HeaderSection>

      {/* Overlay and panel are siblings of HeaderSection — outside its backdrop-filter
          stacking context, so z-index works correctly against the header */}
      <DrawerOverlay $open={visible} onClick={toggleButton} />
      <DrawerPanel $open={visible}>
        <DrawerClose onClick={toggleButton} aria-label="Close menu">✕</DrawerClose>
        <div style={{ marginBottom: "2.5rem", marginTop: "0.5rem" }}>
          <Label>MyExamNation</Label>
        </div>
        <Menu style={{ flexDirection: "column", alignItems: "flex-start", gap: "1.5rem" }}>
          {MenuItem()}
          <DrawerDivider />
          {user ? (
            <>
              <DrawerUserEmail>{user.email}</DrawerUserEmail>
              <NavItem onClick={() => goTo("/progress")}>My Progress</NavItem>
              <NavItem onClick={() => goTo("/account")}>My Account</NavItem>
              <NavItem onClick={handleLogout}>Sign out</NavItem>
            </>
          ) : (
            <NavItem onClick={() => goTo("/signin")}>Sign in</NavItem>
          )}
        </Menu>
      </DrawerPanel>
    </>
  );
};

export default withTranslation()(Header);
